import axios from 'axios';
import _ from 'lodash';
import { store } from '@/scripts/store';
import {
  isArray, getQcloudSign, getQcloudApiCommonParams, mergeData, queryParamsToString,
} from '@/scripts/tools/utils';
import { V4RegionMap } from '@/scripts/tools/config';
import cosAuth from '@/plugins/cos_sdk/src/cos-auth';
import cos from './cos/index';
import user from './user';

axios.defaults.timeout = 15000;
// axios.defaults.baseURL = window.location.baseURL;
axios.defaults.baseURL = store.cosbrowserServerUrl;
// 添加响应拦截器
axios.interceptors.response.use(
  response => {
    const { data, status } = response;
    const statusSuccess = Math.floor(status / 100) === 2; // 200 202 204 206
    if (statusSuccess) {
      if (!data.code) {
        return data.data || data;
      }
      return Promise.reject(data);
    }
    return Promise.reject(data);
  },
  error => {
    if (error.code) {
      return Promise.reject(error);
    }
    const err = { code: -1, message: '网络繁忙，请稍候再试' };
    return Promise.reject(err);
  },
);

const tools = {
  getCloudApiHost(name) {
    return `${name}.tencentcloudapi.com`;
  },
};

/**
 * 列出文件列表
 * @param {*} bucketInfo
 * @param {*} query
 */
async function listObjects(bucketInfo, query) {
  // 默认值
  if (!query.MaxKeys) {
    query.MaxKeys = 200;
  }
  if (!query.Marker) {
    query.Marker = '';
  }
  const credential = await user.getCredential();
  const SecretId = credential.SecretId || credential.tmpSecretId;
  const SecretKey = credential.SecretKey || credential.tmpSecretKey;

  const authParams = {
    SecretId,
    SecretKey,
    Method: 'GET',
    Query: {
      prefix: query.Prefix,
      marker: query.Marker,
      delimiter: query.Delimiter,
      'max-keys': query.MaxKeys,
    },
  };
  if (!query.Delimiter) {
    delete authParams.Query.delimiter;
  }
  let Authorization = cos.getAuth(authParams);
  if (credential.token) {
    Authorization += `&x-cos-security-token=${credential.token}`;
  }
  const params = {
    ...bucketInfo,
    ...query,
    Authorization,
  };
  return axios.get('/cos/object/list', { params }).then(res => {
    const { ListBucketResult } = res;

    let Contents = ListBucketResult.Contents || [];
    let CommonPrefixes = ListBucketResult.CommonPrefixes || [];

    Contents = isArray(Contents) ? Contents : [Contents];
    CommonPrefixes = isArray(CommonPrefixes) ? CommonPrefixes : [CommonPrefixes];
    return Promise.resolve({ ...ListBucketResult, Contents, CommonPrefixes });
  });
}

/**
 * 列出存储桶列表
 * @param {Object} opt
 */
async function listBuckets() {
  const credential = await user.getCredential();
  const SecretId = credential.SecretId || credential.tmpSecretId;
  const SecretKey = credential.SecretKey || credential.tmpSecretKey;

  const authParams = {
    SecretId,
    SecretKey,
    Method: 'GET',
  };
  let Authorization = cos.getAuth(authParams);
  if (credential.token) {
    Authorization += `&x-cos-security-token=${credential.token}`;
  }

  const params = {
    Authorization,
  };
  return axios.get('/cos/bucket/list', { params }).then(res => {
    let buckets = (res
        && res.ListAllMyBucketsResult
        && res.ListAllMyBucketsResult.Buckets
        && res.ListAllMyBucketsResult.Buckets.Bucket)
      || [];
    buckets = isArray(buckets) ? buckets : [buckets];
    const owner = (res && res.ListAllMyBucketsResult && res.ListAllMyBucketsResult.Owner) || {};

    return Promise.resolve({
      Buckets: buckets,
      Owner: owner,
    });
  });
}

/**
 * 配饰存储桶权限 V4 双写
 * @param {Object} params
 */
async function putV4BucketAcl(params) {
  const { Bucket, Region, ACL } = params;
  const credential = await user.getCredential();
  const SecretId = credential.SecretId || credential.tmpSecretId;
  const SecretKey = credential.SecretKey || credential.tmpSecretKey;

  const authParams = {
    Version: '4.0', // 计算 V4 签名
    Bucket,
    SecretId,
    SecretKey,
  };

  let Authorization = cosAuth(authParams);
  if (credential.token) {
    Authorization += `&x-cos-security-token=${credential.token}`;
  }
  const reqParams = {
    Authorization: decodeURIComponent(Authorization),
    Bucket,
    Region,
    ACL,
  };
  return axios.post('/cos/bucket/putV4BucketAcl', reqParams);
}

/**
 * 获取用户信息
 */
async function getUserInfo() {
  const credential = await user.getCredential();
  const SecretId = credential.SecretId || credential.tmpSecretId;
  const SecretKey = credential.SecretKey || credential.tmpSecretKey;
  const commonParams = getQcloudApiCommonParams(SecretId);

  const opt = {
    Action: 'GetUserInfo',
    Version: '2018-06-22',
  };

  const requestData = {
    ...commonParams,
    ...opt,
  };
  const host = tools.getCloudApiHost('cosbrowser');
  if (credential.token) {
    requestData.Token = credential.token;
  }
  requestData.Signature = getQcloudSign('GET', requestData, SecretKey, host);
  return axios.post('/cos/others/getUserInfo', requestData);
}

/**
 * 获取标签列表
 * @param {Object} params
 */
async function getBucketTagList(params) {
  const credential = await user.getCredential();
  const SecretId = credential.SecretId || credential.tmpSecretId;
  const SecretKey = credential.SecretKey || credential.tmpSecretKey;
  const commonParams = getQcloudApiCommonParams(SecretId);

  // 全量拉取时data.Tags 且可能重复
  // 非全量拉去时data.Rows
  // 使用请自行斟酌
  const { isFull } = params;
  const opt = {
    // 必填参数
    Action: isFull ? 'DescribeTags' : 'DescribeResourceTags',
    Version: '2018-08-13',
    // 选填参数
    Limit: 20000,
  };

  const requestData = {
    ...commonParams,
    Region: 'ap-guangzhou',
    ...opt,
  };
  const host = tools.getCloudApiHost('tag');
  if (credential.token) {
    requestData.Token = credential.token;
  }
  requestData.Signature = getQcloudSign('POST', requestData, SecretKey, host);
  requestData.Product = 'tag';

  return axios.post('/cos/others/cloudapi', requestData);
}

async function getBucketByTags(params) {
  const credential = await user.getCredential();
  const SecretId = credential.SecretId || credential.tmpSecretId;
  const SecretKey = credential.SecretKey || credential.tmpSecretKey;
  const commonParams = getQcloudApiCommonParams(SecretId);

  const { Tag } = params;
  let opt = {
    // 必填参数
    Action: 'DescribeResourcesByTags',
    Version: '2018-08-13',
    TagFilters: [{ TagKey: Tag.split(';')[0], TagValue: [Tag.split(';')[1] || null] }],
    // 选填参数
    ServiceType: 'COS',
    Limit: 5000,
  };
  opt = mergeData(opt);

  const requestData = {
    ...commonParams,
    ...opt,
  };
  const host = tools.getCloudApiHost('tag');
  if (credential.token) {
    requestData.Token = credential.token;
  }
  requestData.Signature = getQcloudSign('POST', requestData, SecretKey, host);
  requestData.Product = 'tag';

  return axios.post('/cos/others/cloudapi', requestData);
}

/**
 * 检查存储桶是否存在 CDN 域名
 * @param {Object} params
 */
async function isCdnHostExist(params) {
  const credential = await user.getCredential();
  const SecretId = credential.SecretId || credential.tmpSecretId;
  const SecretKey = credential.SecretKey || credential.tmpSecretKey;
  const commonParams = getQcloudApiCommonParams(SecretId);

  const { Bucket, Region } = params;
  const defaultCdnDomain = `${Bucket}.file.myqcloud.com`;
  const restfulDomain = `${Bucket}.cos.${Region}.myqcloud.com`;
  const websiteDomain = `${Bucket}.cos-website.${Region}.myqcloud.com`;

  let opt = {
    Action: 'DescribeDomains',
    Version: '2018-06-06',
    Filters: [{ Name: 'origin', Value: [restfulDomain, websiteDomain] }],
  };
  opt = mergeData(opt);

  const requestData = {
    ...commonParams,
    Region: 'ap-guangzhou',
    ...opt,
  };
  const host = tools.getCloudApiHost('cdn');
  if (credential.token) {
    requestData.Token = credential.token;
  }
  requestData.Signature = getQcloudSign('POST', requestData, SecretKey, host);
  requestData.Product = 'cdn';

  return axios.post('/cos/others/cloudapi', requestData).then(res => {
    let isExist = false;
    isExist = _.some(res.Domains || [], item => {
      const { Domain } = item;
      return (
        // 默认加速域名未关闭 || 存在自定义加速域名
        (Domain === defaultCdnDomain && item.Status !== 'offline') || Domain !== defaultCdnDomain
      );
    });
    return Promise.resolve(isExist);
  });
}

async function getBucketCustomizeCdnHostNew(params) {
  const credential = await user.getCredential();
  const SecretId = credential.SecretId || credential.tmpSecretId;
  const SecretKey = credential.SecretKey || credential.tmpSecretKey;
  const commonParams = getQcloudApiCommonParams(SecretId);

  const { Bucket, Region } = params;
  const defaultCdnDomain = `${Bucket}.file.myqcloud.com`;
  const restfulDomain = `${Bucket}.cos.${Region}.myqcloud.com`;
  const websiteDomain = `${Bucket}.cos-website.${Region}.myqcloud.com`;
  // V4版本兼容
  const V4Region = V4RegionMap[Region];
  const V4Domain = `${Bucket}.cos${V4Region}.myqcloud.com`;

  let opt = {
    Action: 'DescribeDomains',
    Version: '2018-06-06',
    Filters: [{ Name: 'origin', Value: [defaultCdnDomain, restfulDomain, websiteDomain, V4Domain] }],
  };
  opt = mergeData(opt);

  const requestData = {
    ...commonParams,
    Region: 'ap-guangzhou',
    ...opt,
  };
  const host = tools.getCloudApiHost('cdn');
  if (credential.token) {
    requestData.Token = credential.token;
  }
  requestData.Signature = getQcloudSign('POST', requestData, SecretKey, host);
  requestData.Product = 'cdn';
  return axios.post('/cos/others/cloudapi', requestData).then(res => {
    const Domains = res.Domains.filter(item => item.Status === 'online');
    const defaultCdnList = [];
    const customCdnList = [];
    Domains.forEach(item => {
      const _item = {
        type: 'cdn',
        domain: item.Domain,
        cname: item.Cname,
        status: item.Status,
      };
      if (item.Domain === defaultCdnDomain) {
        defaultCdnList.push(_item);
      } else {
        customCdnList.push(_item);
      }
    });

    const result = { defaultCdnList, customCdnList };
    return Promise.resolve(result);
  });
}

async function getSts(params) {
  const credential = await user.getCredential();
  const SecretId = credential.SecretId || credential.tmpSecretId;
  const SecretKey = credential.SecretKey || credential.tmpSecretKey;
  const commonParams = getQcloudApiCommonParams(SecretId);

  const opt = {
    Action: 'GetFederationToken',
    Version: '2018-08-13',
    Name: 'cosbrowser',
  };

  let { Region } = params;

  const { Policy, DurationSeconds } = params;
  const requestData = {
    ...commonParams,
    Region,
    ...opt,
    Policy,
    DurationSeconds,
  };
  let host = tools.getCloudApiHost('sts');
  if (Region.indexOf('-fsi') > -1) {
    host = tools.getCloudApiHost(`sts.${Region}`);
  } else {
    Region = 'ap-guangzhou';
  }
  if (credential.token) {
    requestData.Token = credential.token;
  }
  requestData.Signature = getQcloudSign('POST', requestData, SecretKey, host);
  if (Region === 'ap-guangzhou') {
    requestData.Product = 'sts';
  } else {
    requestData.Product = `sts.${Region}`;
  }
  return axios.post('/cos/others/cloudapi', requestData);
}

async function importProject(params) {
  const {
    Bucket, Region, Prefix, url, sid, skey, token,
  } = params;
  const data = {
    bucket: Bucket,
    region: Region,
    prefix: Prefix,
    fileUrl: url,
    sid,
    skey,
    token,
  };
  const options = {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    data: queryParamsToString(data),
    url: 'https://service-psscj12e-1251668577.hk.apigw.tencentcs.com/import',
    timeout: 900000,
  };
  return axios(options)
    .catch(err => {
      if (err.code) {
        return Promise.reject(err);
      }
      return { code: 'RequestError' };
    });
}

function getStsWhileImport(filename) {
  return axios({
    method: 'GET',
    url: '/api/import/getSts',
    params: { filename },
  }).catch(err => {
    if (err.error) {
      return Promise.reject(err.error);
    }
    const error = { code: 'RequestError' };
    return Promise.reject(error);
  });
}

async function getService() {
  const credential = await user.getCredential();
  const SecretId = credential.SecretId || credential.tmpSecretId;
  const SecretKey = credential.SecretKey || credential.tmpSecretKey;
  const commonParams = getQcloudApiCommonParams(SecretId);

  const opt = {
    // 必填参数
    Action: 'ListBuckets',
    Version: '2021-02-24',
  };

  const requestData = {
    ...commonParams,
    ...opt,
  };

  const host = tools.getCloudApiHost('cos');
  if (credential.token) {
    requestData.Token = credential.token;
  }
  requestData.Signature = getQcloudSign('POST', requestData, SecretKey, host);
  requestData.Product = 'cos';

  return axios.post('/cos/others/cloudapi', requestData);
}

export default {
  listObjects,
  listBuckets,
  putV4BucketAcl,
  getUserInfo,
  getBucketTagList,
  getBucketByTags,
  isCdnHostExist,
  getBucketCustomizeCdnHostNew,
  getSts,
  getService,
  addCosBrowserTask: params => axios.post('/cosbrowser/api/task/add', params),
  checkCosBrowserTask: params => axios.get('/cosbrowser/api/task/check', { params }),
  saveSharedFilesList: params => axios.post('/api/share/save', params),

  importProject,
  getStsWhileImport,
};
